import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Map,
  Menu,
  Settings as SettingsIcon,
  UserCog,
  ClipboardList,
  X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { useIsAdmin } from "@/hooks/useCurrentUser";
import { useAuthStore } from "@/store/auth";
import { useRealtimeSync } from "@/hooks/useRealtimeSync";
import { useAppointmentReminders } from "@/hooks/useAppointmentReminders";
import { UserMenu } from "@/components/layout/UserMenu";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  adminOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/leads", label: "Leads", icon: Users },
  { to: "/routes", label: "Routes", icon: Map },
  { to: "/team", label: "Team", icon: UserCog, adminOnly: true },
  { to: "/activity", label: "Activity", icon: ClipboardList, adminOnly: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export function AppShell() {
  const isAdmin = useIsAdmin();
  const isAuthed = useAuthStore((s) => s.status === "authed");
  const hasMfa = useAuthStore((s) => s.user?.hasMfa ?? false);
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // One global Realtime subscription powers cross-tab + cross-user
  // synchronization of counts, lists, KPIs, and activity. Mounted here so it
  // stays alive across route changes and tears down only on sign-out.
  useRealtimeSync(isAuthed);
  // In-app appointment reminders (due-within-the-hour toasts) — same
  // lifetime as the realtime sync: any page, torn down on sign-out.
  useAppointmentReminders(isAuthed);
  const items = NAV.filter((n) => !n.adminOnly || isAdmin);

  // Close the mobile drawer on route change so tapping a nav link doesn't
  // leave the overlay covering the destination page.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll when the drawer is open so the underlying page doesn't
  // scroll behind the overlay on touch devices.
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  // Mandatory-MFA gate. Any signed-in user without a verified TOTP factor
  // gets routed to /onboarding/mfa before the shell renders. There's no
  // way past this — security is policy, not preference. The auth store
  // re-hydrates hasMfa on successful verify; once true, this branch falls
  // through and the rest of the app renders.
  if (isAuthed && !hasMfa) {
    return <Navigate to="/onboarding/mfa" replace />;
  }

  const sidebarContent = (
    <>
      <div className="flex items-center justify-between gap-3 px-5 py-5">
        <div className="flex items-center gap-3">
          <img src="/favicon.svg" alt="" className="h-9 w-9" />
          <div className="leading-none">
            <div className="text-[15px] font-semibold tracking-tight text-slate-900" style={{ letterSpacing: "-0.02em" }}>
              Routes Made
            </div>
            <div className="mt-1 text-[11px] font-bold tracking-[0.22em] text-brand-700">SIMPLE</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setDrawerOpen(false)}
          className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700 md:hidden"
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <nav className="flex-1 space-y-1 px-2">
        {items.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition",
                isActive
                  ? "bg-brand-50 text-brand-700"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
              )
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-200 px-4 py-3 text-[11px] text-slate-400">
        Routes Made Simple · Beta
      </div>
    </>
  );

  return (
    <div className="flex h-full">
      {/* Desktop sidebar — always visible from md+ */}
      <aside className="hidden w-60 flex-col border-r border-slate-200 bg-white md:flex">
        {sidebarContent}
      </aside>

      {/* Mobile drawer — overlay + slide-in panel */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside className="absolute inset-y-0 left-0 flex w-64 max-w-[80%] flex-col border-r border-slate-200 bg-white shadow-xl">
            {sidebarContent}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 sm:px-6">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="-ml-1 rounded-md p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 md:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 md:hidden">
            <img src="/favicon.svg" alt="" className="h-6 w-6" />
            <span className="text-sm font-semibold tracking-tight text-slate-900">Routes Made Simple</span>
          </div>
          <div className="ml-auto">
            <UserMenu />
          </div>
        </header>
        <main className="flex-1 overflow-auto bg-slate-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
